/**
 * Filter state for the schedule calendar. Mirrors the mobile app's
 * ScheduleFilters shape (apps/app-mobile/store/useScheduleStore.ts) so the
 * filtering logic stays consistent across platforms.
 *
 * Location / Sport / Skill Level / Coach are multi-select string arrays.
 * Age Bracket is single-select.
 */

import { useCallback, useMemo, useState } from 'react';

import type { ScheduleEvent } from '../../services/schedule';

export interface ScheduleFilters {
  ageBracket?: string;
  sport?: string[];
  location?: string[];
  skillLevel?: string[];
  coach?: string[];
}

export type ArrayFilterKey = 'sport' | 'location' | 'skillLevel' | 'coach';

export function useScheduleFilters() {
  const [filters, setFilters] = useState<ScheduleFilters>({});

  const toggleArrayFilter = useCallback((key: ArrayFilterKey, value: string) => {
    setFilters((prev) => {
      const current = prev[key] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      const updated = { ...prev };
      if (next.length === 0) {
        delete updated[key];
      } else {
        updated[key] = next;
      }
      return updated;
    });
  }, []);

  const setAgeBracket = useCallback((value: string | undefined) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (!value) {
        delete updated.ageBracket;
      } else {
        updated.ageBracket = value;
      }
      return updated;
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const activeCount = useMemo(() => {
    let n = 0;
    if (filters.ageBracket) n += 1;
    n += filters.sport?.length ?? 0;
    n += filters.location?.length ?? 0;
    n += filters.skillLevel?.length ?? 0;
    n += filters.coach?.length ?? 0;
    return n;
  }, [filters]);

  return {
    filters,
    setFilters,
    toggleArrayFilter,
    setAgeBracket,
    clearFilters,
    activeCount,
  };
}

/**
 * Apply the active filters to a list of events. Pure helper; safe to memoize
 * the result at the call site.
 */
export function applyScheduleFilters(
  events: ScheduleEvent[],
  filters: ScheduleFilters
): ScheduleEvent[] {
  if (
    !filters.ageBracket &&
    !filters.sport?.length &&
    !filters.location?.length &&
    !filters.skillLevel?.length &&
    !filters.coach?.length
  ) {
    return events;
  }

  return events.filter((evt) => {
    if (filters.ageBracket && evt.age_bracket_name !== filters.ageBracket) return false;
    if (filters.sport?.length && !(evt.sport_name && filters.sport.includes(evt.sport_name)))
      return false;
    if (
      filters.location?.length &&
      !(evt.location_name && filters.location.includes(evt.location_name))
    )
      return false;
    if (
      filters.skillLevel?.length &&
      !(evt.skill_level_name && filters.skillLevel.includes(evt.skill_level_name))
    )
      return false;
    const coachFilter = filters.coach;
    if (coachFilter?.length) {
      const names = evt.instructor_names ?? [];
      if (!names.some((n) => coachFilter.includes(n))) return false;
    }
    return true;
  });
}

/**
 * Derive the set of available option values from the unfiltered events list,
 * so the drawer always shows every option the user can select.
 */
export function deriveFilterOptions(events: ScheduleEvent[]) {
  const locations = new Set<string>();
  const sports = new Set<string>();
  const skillLevels = new Set<string>();
  const coaches = new Set<string>();
  const ageBrackets = new Set<string>();

  for (const e of events) {
    if (e.location_name) locations.add(e.location_name);
    if (e.sport_name) sports.add(e.sport_name);
    if (e.skill_level_name) skillLevels.add(e.skill_level_name);
    if (e.age_bracket_name) ageBrackets.add(e.age_bracket_name);
    if (Array.isArray(e.instructor_names)) {
      for (const n of e.instructor_names) if (n) coaches.add(n);
    }
  }

  const sorted = (set: Set<string>) => Array.from(set).sort((a, b) => a.localeCompare(b));

  return {
    locations: sorted(locations),
    sports: sorted(sports),
    skillLevels: sorted(skillLevels),
    coaches: sorted(coaches),
    ageBrackets: sorted(ageBrackets),
  };
}
